import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, openModal } from '@vario-connect/frontend-design';
import { renderMappingTab } from '../tabs/otto-list-mapping.js';
import { renderOptionalFieldsTab } from '../tabs/otto-list-optional-fields.js';
import { renderVariantsTab } from '../tabs/otto-list-variants.js';
import { renderVariantDataGrid } from '../tabs/otto-variant-data.js';
import { renderVariantFieldOverrides } from '../tabs/otto-variant-field-overrides.js';
import { renderImagesTab } from '../tabs/otto-list-images.js';
import { renderStockBlock } from '../tabs/otto-list-stock.js';
import { openCategoryPicker, type CategoryPick } from './category-picker.js';
import { renderRequiredAttrsLine, type RequiredAttrsSummary } from './required-attrs.js';
import { httpErrorText } from '../../lib/session-expiry.js';

// ============================================================================
// Geteiltes Otto-Listing-Detail-Pane — gerendert vom Lister (Master-Detail,
// rechte Spalte) UND vom Vario-Slot listing-status (Configure-Modus im Artikel).
//
// Otto-Modell (Spec 2026-06-18): Produkt = productReference + variations[];
// single Storefront 'otto'; kein Push in diesem Spec (Status endet bei 'mapped').
// Overview = Vario-Daten + Otto-Kategorie + Brand-Picker (otto.brands) +
// „push-bereit markieren" + Archivieren. Preview ruft die Edge-Function
// otto-listing-preview (voller Otto-V5-Envelope). Mapping/Bilder/Varianten:
// Platzhalter bis Schritt 2 des Frontend-Follow-ups.
// ============================================================================

/** Zweistufige Bestätigung (Projektregel: keine window.confirm/alert).
 *  Erster Klick „schärft" den Button, nach 4s ohne zweiten Klick Reset. */
export function wireTwoStepConfirm(
  btn: HTMLButtonElement,
  armedHtml: string,
  onConfirm: () => void,
): void {
  const idleHtml = btn.innerHTML;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const disarm = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    btn.dataset['armed'] = '';
    btn.innerHTML = idleHtml;
    btn.classList.remove('klst-btn-armed');
  };
  btn.addEventListener('click', () => {
    if (btn.dataset['armed'] === '1') { disarm(); onConfirm(); return; }
    btn.dataset['armed'] = '1';
    btn.innerHTML = armedHtml;
    btn.classList.add('klst-btn-armed');
    timer = setTimeout(disarm, 4000);
  });
}

export interface DetailPaneCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

export interface DetailPaneHost {
  kind: 'lister' | 'slot';
  onListingChanged(): void;
  onDeleted(): void;
  /** Leichter Refresh nach Speichern (linke Liste / Status), OHNE das Detail-Pane neu zu bauen —
   *  so bleiben der aktive Tab und die Eingaben erhalten. */
  onSaved?(): void;
  /** Die Pflichtattribut-Prüfung hat ihr Ergebnis am Listing gespeichert. Der Lister lädt daraufhin
   *  seine Liste neu (Chip). onSaved allein reicht nicht: die Prüfung dauert einige Sekunden und
   *  endet erst nach dem Neuladen der Liste. */
  onRequiredAttrsChecked?(): void;
  /** Reicht den Speichern-Handler des AKTIVEN Tabs an den Host (z. B. Slot-Titelleiste): save-Fn,
   *  Button-Label und Sichtbarkeit. Wird beim ersten Render UND bei jedem Tab-Wechsel aufgerufen,
   *  damit ein Save-Button außerhalb der Detailfläche Label/Sichtbarkeit spiegeln kann. */
  onSaveHandler?(info: { save: () => Promise<void>; label: string; visible: boolean }): void;
  /** Wenn true, rendert der Pane KEINEN eigenen „Speichern"-Button — der Host führt ihn selbst
   *  (z. B. in einer Titelleiste) und wird über onSaveHandler gespeist. Für kind:'lister' im
   *  Vario-Slot, wo die Lister-Titelleiste Speichern+Senden hält (wie kind:'slot'). */
  externalSave?: boolean;
}

type DetailTab = 'overview' | 'optional' | 'variants' | 'images' | 'stock' | 'preview';

/** Pro Tab: Button-Label, Erfolgs-Toast und die Saver-IDs, die dieser Tab speichert.
 *  null = kein Speichern (Vorschau). Der Speichern-Button oben rechts benennt sich danach um
 *  und führt NUR die Saver des aktiven Tabs aus (kein globales „alles speichern" mehr). */
interface TabSaveCfg { label: string; done: string; saverIds: string[] }
const TAB_SAVE: Record<DetailTab, TabSaveCfg | null> = {
  overview: { label: 'Übersicht speichern', done: 'Übersicht gespeichert', saverIds: ['mapping'] },
  variants: { label: 'Varianten speichern', done: 'Varianten gespeichert', saverIds: ['variant-data', 'variant-attrs'] },
  optional: { label: 'Optionale Felder speichern', done: 'Optionale Felder gespeichert', saverIds: ['optional'] },
  images:   { label: 'Bilder speichern', done: 'Bilder gespeichert', saverIds: ['images'] },
  stock:    { label: 'Bestand & Lager speichern', done: 'Bestand & Lager gespeichert', saverIds: ['stock'] },
  preview:  null,
};
const CHILD_TAB_SAVE: Record<string, TabSaveCfg> = {
  overview: { label: 'Felder speichern', done: 'Felder gespeichert', saverIds: ['field-overrides'] },
  optional: { label: 'Variantenattribute speichern', done: 'Variantenattribute gespeichert', saverIds: ['optional'] },
  images:   { label: 'Bilder speichern', done: 'Bilder gespeichert', saverIds: ['images'] },
};

/** Otto ist einmarktig — der Storefront ist immer 'otto'. Map nur für Anzeige-
 *  Kompatibilität (Master/Slot importieren das Symbol). */
export const STOREFRONT_LABELS: Record<string, string> = { otto: 'OTTO Market' };

export type ListingStatus = 'pending' | 'mapping' | 'mapped' | 'pushing' | 'pushed' | 'error' | 'archived';

export const STATUS_PILL: Record<ListingStatus, { cls: string; label: string }> = {
  pending:  { cls: 'v-pill--info',  label: 'Neu' },
  mapping:  { cls: 'v-pill--warn',  label: 'In Bearbeitung' },
  mapped:   { cls: 'v-pill--ok',    label: 'Bereit' },
  pushing:  { cls: 'v-pill--info',  label: 'Wird übertragen' },
  pushed:   { cls: 'v-pill--ok',    label: 'Gelistet' },
  error:    { cls: 'v-pill--error', label: 'Fehler' },
  archived: { cls: 'v-pill--muted', label: 'Archiviert' },
};

export interface ListingDetail {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  vario_category_id: string | null;
  vario_category_name: string | null;
  otto_category: string | null;
  otto_brand_id: string | null;
  vario_listing_snapshot: Record<string, unknown> | null;
  is_variant_parent: boolean;
  is_variant_child: boolean;
  parent_listing_id: string | null;
  mapping_override: Record<string, unknown> | null;
  status: ListingStatus;
  last_error: string | null;
  first_seen_at: string;
  last_scan_at: string;
  updated_at: string;
}

interface BrandRow { id: string; name: string }

// ============================================================================
// Entry-Funktion
// ============================================================================

export async function renderListingDetailPane(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  listingId: string,
  paneHost: DetailPaneHost,
): Promise<void> {
  mount.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Details…</div>`;
  const { data, error } = await ctx.supabase.rpc('otto_get_listing_detail', { _id: listingId });
  if (error) {
    mount.innerHTML = `<div class="klst-loading klst-loading--error">Fehler: ${escapeHtml(error.message)}</div>`;
    return;
  }
  const detail = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
  if (!detail) {
    mount.innerHTML = `<div class="klst-loading">Listing nicht gefunden.</div>`;
    return;
  }

  // Per-Pane Save-Registry: jeder geladene Tab registriert seine Speichern-Logik unter
  // einer stabilen ID; der globale „Speichern"-Button (oben rechts) führt alle aus.
  // Bei Tab-Re-Render überschreibt derselbe id-Eintrag den alten Saver (kein stale closure,
  // da die Saver-Funktionen den DOM-State erst zur Ausführungszeit lesen).
  const savers = new Map<string, () => Promise<void>>();
  const registerSaver = (id: string, fn: () => Promise<void>) => { savers.set(id, fn); };

  // Nach erfolgreichem Speichern: optionale Felder als JSON-EAV zurückschreiben (async Worker-Job,
  // nicht blockierend) + leichter Peripherie-Refresh (Liste/Status) OHNE Pane-Neuaufbau, damit der
  // aktive Tab und die Eingaben erhalten bleiben (Fix: kein Sprung in „Übersicht", kein „Feld leer").
  const postSave = () => {
    void Promise.resolve(ctx.supabase.rpc('otto_enqueue_optional_eav_writeback', { _listing_id: detail.id }))
      .then(({ error: enqErr }) => { if (enqErr) console.warn('[otto] optional-eav enqueue failed:', enqErr.message); })
      .catch((e) => console.warn('[otto] optional-eav enqueue error:', (e as Error).message));
    paneHost.onSaved?.();
    // Mapping kann Pflichtattribute geschlossen haben → Prüfung neu (nur Hauptartikel mit Kategorie).
    if (!detail.is_variant_child && detail.otto_category) void refreshRequiredAttrs(mount, ctx, detail, paneHost);
  };
  // Führt genau die Saver des aktiven Tabs aus (fehlende/ungeladene ids werden übersprungen).
  const runSavers = async (ids: string[], doneMsg: string): Promise<void> => {
    const fns = ids.map((id) => savers.get(id)).filter((f): f is () => Promise<void> => !!f);
    if (fns.length === 0) { showToast('Nichts zu speichern', 'info'); return; }
    try {
      for (const fn of fns) await fn();
      showToast(doneMsg, 'success');
      postSave();
    } catch (e) { showToast(`Speichern fehlgeschlagen: ${(e as Error).message}`, 'error'); }
  };

  const showSave = paneHost.kind !== 'slot' && !paneHost.externalSave;

  // Variantenkind → reduzierter Lister (Kategorie/Marke vom Vater, nur variantenmögliche Felder).
  if (detail.is_variant_child) {
    mount.innerHTML = renderChildDetail(detail, showSave);
    wireChildPane(mount, ctx, detail, paneHost, registerSaver, runSavers);
    return;
  }

  mount.innerHTML = renderDetail(detail, showSave);
  wireDetailTabs(mount, ctx, detail, paneHost, registerSaver, runSavers);

  // Brand-Picker befüllen + verdrahten.
  void wireBrandPicker(mount, ctx, detail);

  // Kategorie-Picker (Tabelle+Filter-Modal) — Kategorie nachträglich änderbar.
  wireCategoryPicker(mount, ctx, detail, paneHost);

  // Pflichtattribute der OTTO-Kategorie: Prüfung über die Vorschau, Ergebnis oben im Pane.
  wireRequiredAttrLinks(mount);
  if (detail.otto_category) void refreshRequiredAttrs(mount, ctx, detail, paneHost);
}

// ============================================================================
// Pflichtattribute (LEGAL): Statuszeile oben, nur Warnung
// ============================================================================

interface PreviewRequiredAttrs { total: number; missing: Array<{ name: string }> }

function toSummary(r: PreviewRequiredAttrs | null | undefined): RequiredAttrsSummary | null {
  return r ? { total: r.total, missing: r.missing.map((m) => m.name) } : null;
}

function showRequiredAttrs(mount: HTMLElement, s: RequiredAttrsSummary | null, state: 'loading' | 'ready' | 'error' = 'ready'): void {
  const host = mount.querySelector<HTMLDivElement>('#otto-req-attrs');
  if (host) host.innerHTML = renderRequiredAttrsLine(s, state);
}

/** Ruft otto-listing-preview nur für die Pflichtattribut-Prüfung (die Funktion hält das Ergebnis
 *  auch am Listing fest, daraus liest der Lister). */
async function refreshRequiredAttrs(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail, paneHost: DetailPaneHost): Promise<void> {
  if (!mount.querySelector('#otto-req-attrs')) return;
  showRequiredAttrs(mount, null, 'loading');
  try {
    const res = await fetch(`${ctx.supabaseUrl}/functions/v1/otto-listing-preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ listingId: detail.id }),
    });
    const payload = (await res.json().catch(() => ({}))) as { requiredAttributes?: PreviewRequiredAttrs | null; error?: unknown };
    if (!res.ok || payload.error) { showRequiredAttrs(mount, null, 'error'); return; }
    const s = toSummary(payload.requiredAttributes);
    showRequiredAttrs(mount, s, s ? 'ready' : 'error');
    if (s) paneHost.onRequiredAttrsChecked?.();
  } catch {
    showRequiredAttrs(mount, null, 'error');
  }
}

/** Klick auf ein fehlendes Attribut: zur Mapping-Zeile im Tab „Übersicht & Mapping“ springen. */
function wireRequiredAttrLinks(mount: HTMLElement): void {
  mount.addEventListener('click', (ev) => {
    const a = (ev.target as HTMLElement).closest<HTMLAnchorElement>('a[data-req-attr]');
    if (!a) return;
    ev.preventDefault();
    mount.querySelector<HTMLButtonElement>('button[data-detail-tab="overview"]')?.click();
    const key = `attr:${a.dataset['reqAttr'] ?? ''}`;
    const row = [...mount.querySelectorAll<HTMLElement>('[data-field]')].find((el) => el.dataset['field'] === key);
    if (!row) { showToast('Attribut im Mapping nicht gefunden.', 'info'); return; }
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.add('otto-req-flash');
    setTimeout(() => row.classList.remove('otto-req-flash'), 1800);
  });
}

// ============================================================================
// Brand-Picker (nur in den Einstellungen vorausgewählte Marken)
// ============================================================================

async function wireBrandPicker(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail): Promise<void> {
  const currentEl = mount.querySelector<HTMLSpanElement>('#otto-brand-current');
  const pickBtn = mount.querySelector<HTMLButtonElement>('#otto-brand-pick');
  if (!currentEl || !pickBtn) return;

  const renderCurrent = (name: string | null) => {
    const html = name
      ? `<strong>${escapeHtml(name)}</strong>`
      : '<span class="v-text-muted">— keine —</span>';
    currentEl.innerHTML = html;
    // Sichtbare Marken-Pille im Mapping-Meta-Strip direkt mitziehen. wireMetaStrip spiegelt
    // #otto-brand-current nur per 5-Sekunden-Polling — wird die Marke DANACH gewählt, bliebe die
    // Pille sonst auf „— keine —". Direktes Update behebt das timing-unabhängig.
    const metaVal = mount.querySelector<HTMLElement>('#otto-meta-brand-val');
    if (metaVal) metaVal.innerHTML = html;
  };

  // Aktuell gesetzte Marke auflösen (otto.brands ist global lesbar).
  if (detail.otto_brand_id) {
    const { data } = await ctx.supabase
      .schema('otto').from('brands').select('id,name').eq('id', detail.otto_brand_id).maybeSingle();
    renderCurrent((data as BrandRow | null)?.name ?? detail.otto_brand_id);
  } else {
    renderCurrent(null);
  }

  pickBtn.addEventListener('click', () => openBrandModal(ctx, detail, renderCurrent));
}

/** Marken-Auswahl als Modal: zeigt AUSSCHLIESSLICH die in den Einstellungen vorausgewählten
 *  Marken (otto_list_selected_brands). Das Suchfeld filtert lokal über diese Liste — KEIN
 *  Voll-Katalog-Zugriff. Klick wählt + persistiert via otto_set_listing_brand. */
function openBrandModal(
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  onPicked: (name: string | null) => void,
): void {
  const modal = openModal({
    title: 'Marke wählen',
    icon: 'fa-tag',
    width: 560,
    body: `
      <div class="v-row v-row-sm" style="gap:8px;align-items:center;">
        <input id="ob-search" class="v-input" type="search" autocomplete="off"
               placeholder="Marke filtern…" style="flex:1;" />
        <button type="button" id="ob-sort" class="v-button v-button--ghost" title="Nach Name sortieren">
          <span class="v-icon fa-regular fa-arrow-down-a-z"></span> Name
        </button>
      </div>
      <div id="ob-hint" class="v-text-muted" style="font-size:12px;margin:6px 0;">Lade präferierte Marken…</div>
      <div id="ob-results" style="max-height:48vh;overflow:auto;display:flex;flex-direction:column;gap:4px;"></div>`,
    footer: `
      ${detail.otto_brand_id ? '<button type="button" class="v-button v-button--ghost" data-ob-clear><span class="v-icon fa-regular fa-xmark"></span> Marke entfernen</button>' : ''}
      <button type="button" class="v-button" data-modal-cancel>Schließen</button>`,
    onMount: (root) => {
      const search = root.querySelector<HTMLInputElement>('#ob-search')!;
      const hint = root.querySelector<HTMLDivElement>('#ob-hint')!;
      const results = root.querySelector<HTMLDivElement>('#ob-results')!;
      const sortBtn = root.querySelector<HTMLButtonElement>('#ob-sort')!;
      search.focus();

      let sortAsc = true;
      let currentRows: BrandRow[] = [];

      const persist = async (brandId: string | null, name: string | null) => {
        const { error } = await ctx.supabase.rpc('otto_set_listing_brand', {
          _listing_id: detail.id,
          _otto_brand_id: brandId,
        });
        if (error) { showToast(`Marke speichern fehlgeschlagen: ${error.message}`, 'error'); return; }
        detail.otto_brand_id = brandId;
        onPicked(name);
        showToast(brandId ? 'Marke gespeichert.' : 'Marke entfernt.', 'success');
        modal.close();
      };

      root.querySelector<HTMLButtonElement>('[data-ob-clear]')?.addEventListener('click', () => void persist(null, null));

      const paint = () => {
        const rows = [...currentRows].sort((a, b) => sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
        results.innerHTML = rows
          .map((b) => `<button type="button" class="v-button v-button--ghost" data-brand-id="${escapeAttr(b.id)}" data-brand-name="${escapeAttr(b.name)}" style="justify-content:flex-start;text-align:left;${b.id === detail.otto_brand_id ? 'border-color:var(--v-positive);' : ''}">${escapeHtml(b.name)}${b.id === detail.otto_brand_id ? ' ✓' : ''}</button>`)
          .join('');
        results.querySelectorAll<HTMLButtonElement>('[data-brand-id]').forEach((btn) => {
          btn.addEventListener('click', () => void persist(btn.dataset['brandId'] ?? null, btn.dataset['brandName'] ?? null));
        });
      };
      const setRows = (rows: BrandRow[]) => { currentRows = rows; paint(); };

      sortBtn.addEventListener('click', () => {
        sortAsc = !sortAsc;
        sortBtn.innerHTML = `<span class="v-icon fa-regular ${sortAsc ? 'fa-arrow-down-a-z' : 'fa-arrow-down-z-a'}"></span> Name`;
        paint();
      });

      // Auswahl beschränkt sich auf die in den Einstellungen vorausgewählten Marken.
      // Geladen wird genau einmal; das Suchfeld filtert lokal über diese Liste.
      let preferred: BrandRow[] = [];
      let loaded = false;

      const applyFilter = (term: string) => {
        if (!loaded) return;
        if (preferred.length === 0) {
          hint.textContent = 'Keine präferierten Marken — bitte zuerst in den Einstellungen wählen.';
          currentRows = []; results.innerHTML = '';
          return;
        }
        const f = term.trim().toLowerCase();
        const rows = f ? preferred.filter((b) => b.name.toLowerCase().includes(f)) : preferred;
        hint.textContent = f
          ? (rows.length ? `${rows.length} von ${preferred.length} präferierten Marken` : 'Keine Treffer in deinen präferierten Marken.')
          : `${preferred.length} präferierte Marke${preferred.length === 1 ? '' : 'n'} · in den Einstellungen verwalten`;
        setRows(rows);
      };

      const loadPreferred = async () => {
        const { data, error } = await ctx.supabase.rpc('otto_list_selected_brands');
        if (error) { hint.textContent = `Fehler: ${error.message}`; return; }
        preferred = (data ?? []) as BrandRow[];
        loaded = true;
        applyFilter(search.value);
      };

      let timer: ReturnType<typeof setTimeout> | null = null;
      search.addEventListener('input', () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => applyFilter(search.value), 120);
      });
      void loadPreferred();
    },
  });
}

// ============================================================================
// Kategorie-Picker (Tabelle+Filter, otto_list_listable_categories)
// ============================================================================

function wireCategoryPicker(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  paneHost: DetailPaneHost,
): void {
  const currentEl = mount.querySelector<HTMLSpanElement>('#otto-cat-current-detail');
  const pickBtn = mount.querySelector<HTMLButtonElement>('#otto-cat-pick-detail');
  if (!currentEl || !pickBtn) return;

  pickBtn.addEventListener('click', () => openCategoryPicker(ctx.supabase, {
    current: detail.otto_category,
    onPick: (p) => void applyCategory(p),
  }));

  async function applyCategory(p: CategoryPick): Promise<void> {
    // 1. Vario-Kategorie am Listing setzen (PATCH categoryRefs) — otto:c:<group>::<leaf>.
    let varioCategoryId: string | null = null;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/otto-assign-listing-category`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleId: detail.vario_article_id, listingId: detail.vario_listing_id,
          group: p.group, leaf: p.leaf,
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as { varioCategoryId?: string; error?: { message: string } };
      if (!res.ok || payload.error) {
        showToast(`Kategorie setzen fehlgeschlagen: ${payload.error?.message ?? httpErrorText(res.status)}`, 'error');
        return;
      }
      varioCategoryId = payload.varioCategoryId ?? null;
    } catch (e) {
      showToast(`Kategorie setzen fehlgeschlagen: ${(e as Error).message}`, 'error');
      return;
    }
    // 2. Lister-Zeile aktualisieren (nur Kategorie-Spalten, kein Snapshot/Marke/Status).
    const { error } = await ctx.supabase.rpc('otto_set_listing_category', {
      _id: detail.id, _otto_category: p.leaf, _category_name: p.leaf, _vario_category_id: varioCategoryId,
    });
    if (error) { showToast(`Speichern fehlgeschlagen: ${error.message}`, 'error'); return; }
    showToast('OTTO-Kategorie aktualisiert.', 'success');
    // Pane neu rendern, damit Mapping/Varianten-Tabs die neue (kategoriegebundene) Basis ziehen.
    void renderListingDetailPane(mount, ctx, detail.id, paneHost);
    paneHost.onListingChanged();
  }
}

// ============================================================================
// Tab-Wiring
// ============================================================================

function wireDetailTabs(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  paneHost: DetailPaneHost,
  registerSaver: (id: string, fn: () => Promise<void>) => void,
  runSavers: (ids: string[], doneMsg: string) => Promise<void>,
): void {
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');
  const paneSaveBtn = mount.querySelector<HTMLButtonElement>('#otto-pane-save');
  let activeTab: DetailTab = 'overview';

  // Speichern-Button (oben rechts) je aktivem Tab umbenennen / aus-/einblenden und den Handler
  // an den Host spiegeln (Slot-Titelleiste trägt den Button außerhalb des Panes). Vorschau → keiner.
  const updateSaveUi = (tab: DetailTab): void => {
    const cfg = TAB_SAVE[tab];
    if (paneSaveBtn) {
      paneSaveBtn.hidden = !cfg;
      if (cfg) paneSaveBtn.innerHTML = `<span class="v-icon fa-regular fa-floppy-disk"></span> ${cfg.label}`;
    }
    paneHost.onSaveHandler?.({
      save: () => (cfg ? runSavers(cfg.saverIds, cfg.done) : Promise.resolve()),
      label: cfg?.label ?? '',
      visible: !!cfg,
    });
  };

  // Der pane-eigene Button (Lister) speichert immer NUR den aktuellen Tab.
  paneSaveBtn?.addEventListener('click', () => {
    const cfg = TAB_SAVE[activeTab];
    if (cfg) void runSavers(cfg.saverIds, cfg.done);
  });

  if (!tabsBar) { updateSaveUi(activeTab); return; }

  const switchTo = (target: DetailTab) => {
    activeTab = target;
    tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('kl-dtab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });
    updateSaveUi(target);

    if (target === 'preview') { void renderPreview(mount, ctx, detail); return; }

    if (target === 'optional') {
      const optHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="optional"]');
      if (optHost && !optHost.dataset['loaded']) {
        optHost.dataset['loaded'] = '1';
        void renderOptionalFieldsTab(optHost, {
          supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
        }, { ottoCategory: detail.otto_category, varioArticleId: detail.vario_article_id });
      }
    }
    if (target === 'variants') {
      const varHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="variants"]');
      if (varHost && !varHost.dataset['loaded']) {
        varHost.dataset['loaded'] = '1';
        // Oben: Variantendaten-Pflege (EAN + Otto-Preis je Kind). Darunter: Achsen→Otto-Attribut-Mapping.
        varHost.innerHTML = '<div id="otto-var-data"></div><div id="otto-var-mapping"></div>';
        const dataHost = varHost.querySelector<HTMLDivElement>('#otto-var-data');
        const mapHost = varHost.querySelector<HTMLDivElement>('#otto-var-mapping');
        if (dataHost) {
          void renderVariantDataGrid(dataHost, {
            supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
          }, { listingId: detail.id });
        }
        if (mapHost) {
          void renderVariantsTab(mapHost, {
            supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
          }, { ottoCategory: detail.otto_category, varioArticleId: detail.vario_article_id });
        }
      }
    }
    if (target === 'images') {
      const imgHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="images"]');
      if (imgHost && !imgHost.dataset['loaded']) {
        imgHost.dataset['loaded'] = '1';
        void renderImagesTab(imgHost, {
          supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
        }, {
          vario_article_id: detail.vario_article_id,
          mode: detail.is_variant_parent ? 'parent' : 'standalone',
          parentListingId: detail.id,
        });
      }
    }
    if (target === 'stock') {
      const stockHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="stock"]');
      if (stockHost && !stockHost.dataset['loaded']) {
        stockHost.dataset['loaded'] = '1';
        void renderStockBlock(stockHost, {
          supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
        }, { listingId: detail.id, isVariantParent: detail.is_variant_parent, sku: detail.vario_article_number });
      }
    }
  };

  tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => switchTo(btn.dataset['detailTab'] as DetailTab));
  });

  // Mapping ist jetzt Teil der „Übersicht & Mapping"-Ansicht (Default-Tab) → direkt inline laden.
  const mapHost = mount.querySelector<HTMLDivElement>('#otto-overview-mapping');
  if (mapHost) {
    void renderMappingTab(mapHost, {
      supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl,
      session: { token: ctx.session.token }, registerSaver,
    }, { ottoCategory: detail.otto_category, varioArticleId: detail.vario_article_id });
  }

  // Initialen Speichern-Button-Zustand setzen (Default-Tab „Übersicht").
  updateSaveUi(activeTab);
}

// ============================================================================
// Preview — ruft Edge-Function otto-listing-preview (voller Otto-V5-Envelope)
// ============================================================================

async function renderPreview(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail): Promise<void> {
  const host = mount.querySelector<HTMLDivElement>('[data-detail-pane="preview"]');
  if (!host) return;
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Baue Vorschau…</div>`;
  try {
    const res = await fetch(`${ctx.supabaseUrl}/functions/v1/otto-listing-preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ listingId: detail.id }),
    });
    const payload = (await res.json().catch(() => ({}))) as {
      envelope?: unknown;
      fieldPreview?: Array<{ key: string; group: string; sourceType: string; resolvedValue: unknown }>;
      errors?: Array<{ field: string; code: string; message: string }>;
      warnings?: Array<{ field: string; code: string; message: string }>;
      requiredAttributes?: PreviewRequiredAttrs | null;
      error?: { code: string; message: string };
    };
    if (!res.ok || payload.error) {
      const msg = payload.error?.message ?? httpErrorText(res.status);
      host.innerHTML = `<div class="v-pa-md"><div class="v-banner v-banner-danger"><span class="v-icon fa-light fa-circle-xmark"></span><div>Vorschau fehlgeschlagen: ${escapeHtml(msg)}</div></div></div>`;
      return;
    }
    if (payload.requiredAttributes !== undefined) showRequiredAttrs(mount, toSummary(payload.requiredAttributes));
    const errs = payload.errors ?? [];
    const warns = payload.warnings ?? [];
    const fp = payload.fieldPreview ?? [];
    const hasIssues = errs.length > 0 || warns.length > 0;
    const envelopeJson = escapeHtml(JSON.stringify(payload.envelope ?? {}, null, 2));

    host.innerHTML = `
      <div class="v-pa-md">
        ${errs.length ? `<div class="v-banner v-banner-danger klst-detail-error"><span class="v-icon fa-light fa-circle-xmark"></span><div><strong>${errs.length} Fehler:</strong><ul>${errs.map((e) => `<li>${escapeHtml(e.field)}: ${escapeHtml(e.message)}</li>`).join('')}</ul></div></div>` : ''}
        ${warns.length ? `<div class="v-banner v-banner-warn"><span class="v-icon fa-light fa-triangle-exclamation"></span><div>${warns.map((w) => `${escapeHtml(w.field)}: ${escapeHtml(w.message)}`).join('<br>')}</div></div>` : ''}

        <div class="kl-pv-status${hasIssues ? ' is-warn' : ''}">
          <span class="v-icon ${hasIssues ? 'fa-light fa-triangle-exclamation' : 'fa-light fa-circle-check'}"></span>
          ${hasIssues
            ? `${errs.length ? `${errs.length} Fehler` : ''}${errs.length && warns.length ? ', ' : ''}${warns.length ? `${warns.length} Warnungen` : ''} — Envelope prüfen.`
            : 'Keine Fehler — push-bereit, sobald das Mapping vollständig ist.'}
        </div>

        <div class="kl-env" id="kl-pv-env">
          <div class="kl-env-head" id="kl-pv-env-head">
            <span class="v-icon fa-regular fa-chevron-right kl-env-chev"></span>
            <span class="kl-env-label">OTTO-Produkt-Envelope</span>
            <span class="kl-env-hint">JSON anzeigen (für Entwickler)</span>
          </div>
          <div class="kl-env-body">
            <pre class="kl-env-pre">${envelopeJson}</pre>
          </div>
        </div>

        ${fp.length ? `
        <div class="kl-sec-lab">Feld-Vorschau</div>
        <table class="kl-restable"><thead><tr><th>Feld</th><th>Quelle</th><th>Wert</th></tr></thead><tbody>
        ${fp.map((f) => {
          const stypeKey = f.sourceType === 'vario_path' ? 'vario' : 'fixed';
          const val = f.resolvedValue != null && f.resolvedValue !== '' ? String(f.resolvedValue) : null;
          return `<tr>
            <td class="kl-rt-fname">${escapeHtml(f.key)}</td>
            <td><span class="kl-stype kl-stype--${stypeKey}">${escapeHtml(f.sourceType)}</span></td>
            <td class="kl-rt-rval${val == null ? ' is-empty' : ''}">${val != null ? escapeHtml(val) : '—'}</td>
          </tr>`;
        }).join('')}
        </tbody></table>` : ''}
      </div>`;

    // Wire envelope collapse toggle (default: closed)
    const envEl = host.querySelector<HTMLDivElement>('#kl-pv-env');
    const envHead = host.querySelector<HTMLDivElement>('#kl-pv-env-head');
    if (envEl && envHead) {
      envHead.addEventListener('click', () => {
        const isOpen = envEl.classList.toggle('is-open');
        const hint = envHead.querySelector<HTMLSpanElement>('.kl-env-hint');
        if (hint) hint.textContent = isOpen ? 'JSON ausblenden' : 'JSON anzeigen (für Entwickler)';
      });
    }
  } catch (e) {
    host.innerHTML = `<div class="v-pa-md"><div class="v-banner v-banner-danger">Vorschau-Fehler: ${escapeHtml((e as Error).message)}</div></div>`;
  }
}

// ============================================================================
// Render-Markup
// ============================================================================

/** Status-Zone (EINZIGE Statusanzeige der Detailfläche — der Slot zeigt KEIN eigenes
 *  Banner mehr). Ohne Kategorie → Empty-Banner; sonst status-bewusste Zeile (grüner
 *  Haken bei gelistet/bereit, Spinner bei „wird übertragen", rot bei Fehler). */
function renderStatusZone(d: ListingDetail): string {
  if (!d.otto_category) {
    return `
    <div class="kl-empty-banner">
      <span class="v-icon fa-light fa-circle-info"></span>
      <div>Dieser Artikel ist noch nicht im OTTO-Lister. Wähle unten eine OTTO-Kategorie, um ihn aufzunehmen.</div>
    </div>`;
  }
  const cat = `· Kategorie <b>${escapeHtml(d.otto_category)}</b>`;
  const line = (mod: string, icon: string, text: string): string =>
    `<div class="kl-statusline ${mod}"><span class="kl-statusline-ic v-icon fa-light ${icon}"></span><div>${text} ${cat}</div></div>`;
  switch (d.status) {
    case 'pushed':   return line('', 'fa-circle-check', '<b>Bei OTTO gelistet</b>');
    case 'mapped':   return line('', 'fa-circle-check', 'Bereit zum Listen (Mapping vollständig)');
    case 'pushing':  return line('kl-statusline--info', 'fa-spinner-third fa-spin', 'Wird an OTTO übertragen…');
    case 'error':    return line('kl-statusline--error', 'fa-circle-xmark', '<b>Listing fehlgeschlagen</b> — Details im Vorschau-Tab');
    case 'archived': return line('kl-statusline--muted', 'fa-box-archive', 'Archiviert');
    default:         return line('kl-statusline--info', 'fa-circle-info', 'Im OTTO-Lister (in Bearbeitung)');
  }
}

// ============================================================================
// Reduzierter Kind-Lister (Variantenkind): Kategorie + Marke werden im Vater gepflegt
// (read-only); editierbar sind nur die variantenmöglichen Angaben — EAN, Otto-Preis,
// variantenrelevante Attribute (Optionale Felder) und eigene Bilder.
// ============================================================================

function renderChildDetail(d: ListingDetail, showSave: boolean): string {
  const saveRow = showSave
    ? `<div class="kl-pane-actions"><button type="button" class="kl-pane-save" id="otto-pane-save"><span class="v-icon fa-regular fa-floppy-disk"></span> Speichern</button></div>`
    : '';
  return `
    <div class="otto-lv2">
      ${saveRow}
      <div class="v-banner v-banner-info kl-child-note">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Variante <strong>${escapeHtml(d.vario_article_number ?? d.vario_article_id)}</strong> —
        Kategorie &amp; Marke werden im <strong>Hauptartikel</strong> gepflegt. Hier nur die
        variantenspezifischen Angaben.</div>
      </div>

      <div id="klst-detail-tabs" class="kl-dtabs">
        <button type="button" class="kl-dtab kl-dtab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-sliders"></span> Felder
        </button>
        <button type="button" class="kl-dtab" data-detail-tab="optional">
          <span class="v-icon fa-regular fa-list-ul"></span> Variantenattribute
        </button>
        <button type="button" class="kl-dtab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
      </div>

      <div class="kl-dbody">
      <div data-detail-pane="overview">
        <div class="v-pa-md">
          <table class="klst-detail-table"><tbody>
            <tr><th>Otto-Kategorie</th><td>${d.otto_category ? `<strong>${escapeHtml(d.otto_category)}</strong>` : '<span class="v-text-muted">— im Hauptartikel —</span>'} <span class="v-text-muted" style="font-size:11px;">(vom Hauptartikel)</span></td></tr>
            <tr><th>Marke</th><td><span class="v-text-muted">${d.otto_brand_id ? 'im Hauptartikel gesetzt' : '— im Hauptartikel —'}</span></td></tr>
          </tbody></table>
        </div>
        <!-- EAN & Preis als Quelle-Felder (Freifeld/Vario-Feld/Formel/nicht gemappt) je Variante. -->
        <div id="otto-child-eanprice"></div>
      </div>
      <div data-detail-pane="optional" hidden></div>
      <div data-detail-pane="images" hidden></div>
      </div>
    </div>`;
}

function wireChildPane(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  paneHost: DetailPaneHost,
  registerSaver: (id: string, fn: () => Promise<void>) => void,
  runSavers: (ids: string[], doneMsg: string) => Promise<void>,
): void {
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');
  const paneSaveBtn = mount.querySelector<HTMLButtonElement>('#otto-pane-save');
  let activeTab = 'overview';

  const updateSaveUi = (tab: string): void => {
    const cfg = CHILD_TAB_SAVE[tab];
    if (paneSaveBtn) {
      paneSaveBtn.hidden = !cfg;
      if (cfg) paneSaveBtn.innerHTML = `<span class="v-icon fa-regular fa-floppy-disk"></span> ${cfg.label}`;
    }
    paneHost.onSaveHandler?.({
      save: () => (cfg ? runSavers(cfg.saverIds, cfg.done) : Promise.resolve()),
      label: cfg?.label ?? '',
      visible: !!cfg,
    });
  };
  paneSaveBtn?.addEventListener('click', () => {
    const cfg = CHILD_TAB_SAVE[activeTab];
    if (cfg) void runSavers(cfg.saverIds, cfg.done);
  });

  const switchTo = (target: string) => {
    activeTab = target;
    tabsBar?.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('kl-dtab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });
    updateSaveUi(target);
    if (target === 'optional') {
      const optHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="optional"]');
      if (optHost && !optHost.dataset['loaded']) {
        optHost.dataset['loaded'] = '1';
        void renderOptionalFieldsTab(optHost, {
          supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
        }, { ottoCategory: detail.otto_category, varioArticleId: detail.vario_article_id });
      }
    }
    if (target === 'images') {
      const imgHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="images"]');
      if (imgHost && !imgHost.dataset['loaded']) {
        imgHost.dataset['loaded'] = '1';
        void renderImagesTab(imgHost, {
          supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
        }, { vario_article_id: detail.vario_article_id, mode: 'child' });
      }
    }
  };
  tabsBar?.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => switchTo(btn.dataset['detailTab']!));
  });

  // EAN & Preis als Quelle-Felder je Variante (Override gegen das geteilte Profil; Vario-Feld =
  // löst je Variante eigenen Wert auf, Freifeld/Formel/nicht gemappt als Ausnahme). Speichert in
  // otto.listings.mapping_override (nur die Keys ean/price; übrige Overrides bleiben erhalten).
  const eanPriceHost = mount.querySelector<HTMLDivElement>('#otto-child-eanprice');
  if (eanPriceHost) {
    void renderVariantFieldOverrides(eanPriceHost, {
      supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token }, registerSaver,
    }, {
      childListingId: detail.id,
      ottoCategory: detail.otto_category,
      varioArticleId: detail.vario_article_id,
      currentOverride: (detail.mapping_override ?? null) as Record<string, { source_type: 'fixed_value' | 'vario_path' | 'template' | 'unmapped'; vario_path?: string | null; fixed_value?: string | null; template?: string | null }> | null,
      restrictKeys: ['productTitle', 'productDescription', 'bulletPoint1', 'bulletPoint2', 'bulletPoint3', 'bulletPoint4', 'bulletPoint5', 'mpn', 'packagingDimensions', 'sku', 'ean', 'price'],
      title: 'Felder · Quelle je Variante',
    });
  }

  // Initialen Speichern-Button-Zustand setzen (Default-Tab „Felder").
  updateSaveUi(activeTab);
}

function renderDetail(d: ListingDetail, showSave: boolean): string {
  const saveBtn = showSave
    ? `<button type="button" class="kl-pane-save" id="otto-pane-save"><span class="v-icon fa-regular fa-floppy-disk"></span> Speichern</button>`
    : '';
  return `
    <div class="otto-lv2">
      <div class="kl-pane-top">
        ${renderStatusZone(d)}
        ${saveBtn}
      </div>
      <div id="klst-detail-tabs" class="kl-dtabs">
        <button type="button" class="kl-dtab kl-dtab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-sliders"></span> Übersicht &amp; Mapping
        </button>
        ${d.is_variant_parent ? `
        <button type="button" class="kl-dtab" data-detail-tab="variants">
          <span class="v-icon fa-regular fa-list-check"></span> Varianten
        </button>` : ''}
        <button type="button" class="kl-dtab" data-detail-tab="optional">
          <span class="v-icon fa-regular fa-list-ul"></span> Optionale Felder
        </button>
        <button type="button" class="kl-dtab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
        <button type="button" class="kl-dtab" data-detail-tab="stock">
          <span class="v-icon fa-regular fa-warehouse"></span> Bestand &amp; Lager
        </button>
        <button type="button" class="kl-dtab" data-detail-tab="preview">
          <span class="v-icon fa-regular fa-eye"></span> Vorschau
        </button>
      </div>

      <div class="kl-dbody">
      <!-- Übersicht & Mapping (zusammengeführt): oben nur Kategorie + Marke, darunter inline
           das Mapping. Restliche Vario-Daten (Name/EAN/Beschreibung/Maße) ergeben sich aus
           den Mapping-Zeilen + der Vorschau. -->
      <div data-detail-pane="overview">
        ${d.otto_category ? `<div id="otto-req-attrs" class="v-pa-md" style="padding-bottom:0;"></div>` : ''}
        ${d.last_error ? `<div class="v-pa-md" style="padding-bottom:0;"><div class="v-banner v-banner-danger klst-detail-error">
          <span class="v-icon fa-light fa-circle-xmark"></span>
          <div style="white-space:pre-wrap;"><strong>Letzter Fehler</strong>\n${escapeHtml(d.last_error)}</div></div></div>` : ''}
        <!-- Kategorie & Marke werden im Mapping-Meta-Strip (unten) angezeigt; diese Tabelle bleibt
             nur als Träger der Picker-Logik im DOM: der Meta-Strip delegiert auf
             #otto-cat-pick-detail / #otto-brand-pick und wireCategory/BrandPicker brauchen
             #otto-cat-current-detail / #otto-brand-current. Daher ausgeblendet (kein „Abstand
             nach oben" mehr, da der Meta-Strip mit eigenem v-pa-md das erste sichtbare Element ist). -->
        <table class="klst-detail-table" hidden><tbody>
          <tr><th>Otto-Kategorie</th><td>
            <div class="v-row v-row-sm" style="align-items:center;gap:8px;">
              <span id="otto-cat-current-detail">${d.otto_category ? `<strong>${escapeHtml(d.otto_category)}</strong>` : '<span class="v-text-muted">— keine —</span>'}</span>
              <button type="button" id="otto-cat-pick-detail" class="v-button v-button--ghost">
                <span class="v-icon fa-regular fa-folder-tree"></span> Kategorie wählen
              </button>
            </div>
          </td></tr>
          <tr><th>Marke</th><td>
            <div class="v-row v-row-sm" style="align-items:center;gap:8px;">
              <span id="otto-brand-current">${d.otto_brand_id ? '<span class="v-text-muted">Lade…</span>' : '<span class="v-text-muted">— keine —</span>'}</span>
              <button type="button" id="otto-brand-pick" class="v-button v-button--ghost">
                <span class="v-icon fa-regular fa-tag"></span> Marke wählen
              </button>
            </div>
          </td></tr>
        </tbody></table>
        <!-- Mapping inline (bringt eigenen v-pa-md-Wrapper aus renderMappingTab mit). -->
        <div id="otto-overview-mapping"></div>
      </div>

      ${d.is_variant_parent ? `<div data-detail-pane="variants" hidden></div>` : ''}
      <div data-detail-pane="optional" hidden></div>
      <div data-detail-pane="images" hidden></div>
      <div data-detail-pane="stock" hidden></div>
      <div data-detail-pane="preview" hidden></div>
      </div>
    </div>`;
}

// ============================================================================
// Utils
// ============================================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
